import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { auth } from "../firebase"; // Import Firebase auth
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth"; // Import necessary auth functions for login form
import GameDisplay from "./GameDisplay"; // Assuming GameDisplay is in the same folder
import config from "../config"; // Assuming config is one level up
import PlayerFeedbackView from "./PlayerFeedbackView"; // <-- Import PlayerFeedbackView
import GameCreationForm from "./GameCreationForm"; // <-- Import GameCreationForm
import "../styles/GamePage.css";
import "../styles/Login.css"; // Import Login styles for the inline login form

const getApiBaseUrl = () =>
  config.apiUrl || process.env.REACT_APP_API_URL || "http://localhost:3000";

function GamePage() {
  const { gameId } = useParams(); // Get gameId from URL
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(auth.currentUser); // Initialize with current user
  const [isOwner, setIsOwner] = useState(false);
  const [isPlayer, setIsPlayer] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState(null);

  // State for player list (owner view)
  const [playersList, setPlayersList] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState(null);

  // State for inline login form
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // State for leaving game
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState(null);

  // Add state for player submission
  const [submissionText, setSubmissionText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  // ---> Add State for Feedback Submission <---
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSubmitError, setFeedbackSubmitError] = useState(null);

  // ---> Add state for the step timer <---
  const [stepTimeRemaining, setStepTimeRemaining] = useState(null);

  // Extract game state, assigned role, and relevant submissions from gameData
  const currentGameState = gameData?.current_state || "LOBBY";
  const currentRound = gameData?.current_round || 0;
  const currentStep = gameData?.current_step || 0;
  const assignedRole = gameData?.assignedRole;
  const mySubmission = gameData?.mySubmission; // Player's submission for current step

  const hasSubmittedFeedback = gameData?.hasSubmittedFeedback || false; // Assuming backend sends this

  // State for polling error
  const [pollingError, setPollingError] = useState(null);

  // ---> Moved fetchGameUpdates back inside the component scope <---
  const fetchGameUpdates = useCallback(async () => {
    if (!gameId) return;

    try {
      setPollingError(null);
      console.log(
        `[${new Date().toLocaleTimeString()}] GamePage Polling: Fetching /api/games/${gameId}`
      ); // Log fetch start

      // --- Add Auth Header ---
      let headers = {};
      const user = auth.currentUser; // Get current user state
      if (user) {
        try {
          const token = await user.getIdToken();
          headers["Authorization"] = `Bearer ${token}`;
          console.log(
            `[${new Date().toLocaleTimeString()}] GamePage Polling: Using auth token for user ${
              user.uid
            }`
          );
        } catch (tokenError) {
          console.error("Error getting token for polling:", tokenError);
          // Decide how to handle - poll without token? Show error?
        }
      }
      // --- End Add Auth Header ---

      const response = await axios.get(
        `${getApiBaseUrl()}/api/games/${gameId}`,
        { headers } // Pass headers to axios
      );

      if (response.data && response.data.gameExists && response.data.game) {
        console.log(
          `[${new Date().toLocaleTimeString()}] GamePage Polling: Received game data`,
          response.data.game
        );
        // Always set state for now:
        setGameData(response.data.game);
      } else {
        console.warn(
          "Polling received unexpected game data structure:",
          response.data
        );
        setPollingError("Polling failed to get valid game data.");
      }
    } catch (err) {
      console.error("Error during game data polling:", err);
      setPollingError("Polling failed. Will retry.");
    }
  }, [gameId, setPollingError, setGameData]); // Dependencies for the fetch function itself

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        // Reset states if user logs out
        setIsOwner(false);
        setIsPlayer(false);
        // Don't reset gameData here, let fetch handle it
        setShowLoginForm(false);
      }
      // Trigger initial fetch or refetch when auth changes
      fetchGame(); // Call the main fetch function
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Effect for initial fetch of game data (including owner/player status)
  // This runs on mount and when gameId or currentUser changes
  const fetchGame = useCallback(async () => {
    if (!gameId) {
      setError("No game ID provided in URL.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setIsOwner(false); // Reset owner/player status on fetch
    setIsPlayer(false);
    setGameData(null); // Reset game data
    setPlayersList([]); // Reset player list
    setPlayersError(null);

    try {
      let headers = {};
      const userForFetch = auth.currentUser; // Use the latest user state directly
      if (userForFetch) {
        const token = await userForFetch.getIdToken();
        headers["Authorization"] = `Bearer ${token}`;
      }

      console.log(
        `Fetching game ${gameId} with user: ${userForFetch?.uid || "guest"}`,
        headers
      );
      const response = await axios.get(
        `${getApiBaseUrl()}/api/games/${gameId}`,
        { headers }
      );

      if (response.data && response.data.gameExists) {
        console.log("Game data received:", response.data);
        setGameData(response.data.game);
        const ownerStatus = response.data.isOwner || false;
        const playerStatus = response.data.isPlayer || false;
        setIsOwner(ownerStatus);
        setIsPlayer(playerStatus);

        // --- Trigger player list fetch if owner ---
        if (ownerStatus) {
          // Fetch players immediately after confirming ownership
          fetchPlayers(gameId, userForFetch); // Pass gameId and owner user
        }
        // ---
      } else {
        setError("Game not found or invalid data received.");
        setGameData(null);
      }
    } catch (err) {
      console.error("Error fetching game page data:", err);
      setError(
        err.response?.data?.error || err.message || "Failed to load game data."
      );
      setGameData(null);
    } finally {
      setLoading(false);
    }
  }, [gameId]); // Remove currentUser dependency to allow guest access

  useEffect(() => {
    fetchGame(); // Run the main fetch function
  }, [fetchGame]); // Run whenever fetchGame identity changes

  // Function to fetch players (only called by owner)
  const fetchPlayers = async (currentGameId, ownerUser) => {
    if (!ownerUser) return; // Should not happen if called correctly

    setPlayersLoading(true);
    setPlayersError(null);
    try {
      const token = await ownerUser.getIdToken();
      const response = await axios.get(
        `${getApiBaseUrl()}/api/games/${currentGameId}/players`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPlayersList(response.data.players || []);
    } catch (err) {
      console.error("Error fetching players list:", err);
      setPlayersError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load players list."
      );
      setPlayersList([]);
    } finally {
      setPlayersLoading(false);
    }
  };

  // Handle joining the game
  const handleJoinGame = useCallback(async () => {
    const userToJoin = auth.currentUser; // Use latest auth state
    if (!userToJoin) {
      setJoinError("You must be logged in to join.");
      return;
    }
    if (!gameId) {
      setJoinError("Game ID is missing.");
      return;
    }

    setJoinLoading(true);
    setJoinError(null);
    setLeaveError(null); // Clear leave error if joining

    try {
      const token = await userToJoin.getIdToken();
      const response = await axios.post(
        `${getApiBaseUrl()}/api/games/${gameId}/join`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Join game response:", response.data);
      setIsPlayer(true); // Update player status
      setShowLoginForm(false); // Hide login form if it was open
    } catch (err) {
      console.error("Error joining game:", err);
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to join game.";
      setJoinError(errorMessage);
      // Keep login form open if it failed after login attempt
      if (!showLoginForm) {
        setLoginError(errorMessage); // Show error in login form if join failed right after login
      }
    } finally {
      setJoinLoading(false);
    }
  }, [gameId, showLoginForm]); // Include showLoginForm to potentially reset loginError

  // Handle login attempt from the inline form
  const handleLogin = async (event, isSignUp = false) => {
    event.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    setJoinError(null); // Clear join error as well
    setLeaveError(null); // Clear leave error on login attempt

    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, loginEmail, loginPassword);
      } else {
        await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      }
      // Login successful! Auth state change will trigger re-fetch.
      // Now, automatically try to join the game.
      console.log("Login successful, attempting to join game...");
      // Small delay to ensure auth state propagation before joining
      setTimeout(handleJoinGame, 100); // Call handleJoinGame after login
    } catch (err) {
      console.error("Login/Signup error:", err);
      setLoginError(err.message || "Failed to authenticate.");
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle leaving the game
  const handleLeaveGame = async () => {
    if (!currentUser || !gameId) return;

    setLeaveLoading(true);
    setLeaveError(null);
    setJoinError(null); // Clear join error if leaving

    try {
      const token = await currentUser.getIdToken();
      await axios.delete(`${getApiBaseUrl()}/api/games/${gameId}/leave`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Successfully left game.");
      setIsPlayer(false); // Update player status
      // Optionally show a success message to the user
    } catch (err) {
      console.error("Error leaving game:", err);
      setLeaveError(
        err.response?.data?.error || err.message || "Failed to leave game."
      );
    } finally {
      setLeaveLoading(false);
    }
  };

  // Function to format the display state (copied from GameCreationForm)
  const formatGameState = () => {
    switch (currentGameState) {
      case "LOBBY":
        return "Lobby (Waiting to Start)";
      case "ACTIVE":
        return `Round ${currentRound} / Step ${currentStep}`;
      case "ROUND_FEEDBACK":
        return `Round ${currentRound} / Feedback`;
      case "ENDED":
        return "Game Ended";
      default:
        return currentGameState;
    }
  };

  // Effect for polling game state (only depends on gameId and the fetch function)
  useEffect(() => {
    if (!gameId) return; // Don't poll without ID

    console.log(`GamePage: Starting polling interval for gameId ${gameId}`);
    const intervalId = setInterval(() => {
      // Directly call the polling function
      fetchGameUpdates();
    }, 5000);

    return () => {
      console.log(`GamePage: Stopping polling interval for gameId ${gameId}`);
      clearInterval(intervalId);
    };
    // Dependencies: gameId ensures interval restarts if ID changes
    // fetchGameUpdates ensures the latest version of the function is used
  }, [gameId, fetchGameUpdates]);

  // --- NEW Handler for Player Submission ---
  const handleTextSubmit = async (event) => {
    event.preventDefault();
    if (
      !gameId ||
      !submissionText.trim() ||
      isSubmitting ||
      currentGameState !== "ACTIVE"
    )
      return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const token = await auth.currentUser.getIdToken();
      await axios.post(
        `${getApiBaseUrl()}/api/games/${gameId}/submit`,
        { text_content: submissionText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Submission successful.");
      // Clear input and potentially disable it by relying on the next poll
      // which should return the submission in `mySubmission`
      setSubmissionText("");
      setJustSubmitted(true);
    } catch (error) {
      console.error("Error submitting text:", error);
      setSubmitError(
        error.response?.data?.error || error.message || "Failed to submit."
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- End New Handler ---

  // --- Reset submission text when step changes --- also reset justSubmitted
  useEffect(() => {
    setSubmissionText("");
    setSubmitError(null); // Clear error on step change
    setJustSubmitted(false); // <-- Reset flag on step change
  }, [currentRound, currentStep]);

  // --- Reset justSubmitted when mySubmission appears ---
  useEffect(() => {
    if (mySubmission) {
      setJustSubmitted(false);
    }
  }, [mySubmission]);

  // ---> Add Handler for Feedback Submission <---
  const handleFeedbackSubmit = async (allocationsPayload) => {
    if (!gameId || !currentUser || isSubmittingFeedback || hasSubmittedFeedback)
      return;

    console.log(
      "GamePage: handleFeedbackSubmit called with:",
      allocationsPayload
    );
    setIsSubmittingFeedback(true);
    setFeedbackSubmitError(null);

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        `${getApiBaseUrl()}/api/games/${gameId}/allocate`,
        { allocations: allocationsPayload }, // Ensure payload structure matches backend
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Feedback submission successful via GamePage.");
      // Submission successful, trigger immediate refetch to update UI state
      // (including hasSubmittedFeedback flag and potentially updated resources)
      await fetchGameUpdates(); // Await the update
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setFeedbackSubmitError(
        error.response?.data?.error ||
          error.message ||
          "Failed to submit feedback."
      );
    } finally {
      setIsSubmittingFeedback(false);
    }
  };
  // --- End Feedback Submission Handler ---

  // ---> Add useEffect for the step timer <---
  useEffect(() => {
    let timerInterval = null;

    // Check if game is active and we have step time data
    if (currentGameState === "ACTIVE" && gameData?.step_time > 0) {
      // Initialize timer with the full step duration
      setStepTimeRemaining(gameData.step_time);

      // Start the interval
      timerInterval = setInterval(() => {
        setStepTimeRemaining((prevTime) => {
          if (prevTime === null || prevTime <= 1) {
            clearInterval(timerInterval); // Stop interval when time reaches 0 or less
            return 0;
          }
          return prevTime - 1; // Decrement time
        });
      }, 1000); // Run every second
    } else {
      // If game is not active or no step time, clear the timer
      setStepTimeRemaining(null);
    }

    // Cleanup function: Clear interval when component unmounts or dependencies change
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [currentGameState, currentRound, currentStep, gameData?.step_time]); // Dependencies that restart the timer

  // Calculate the list of qualified submissions using useMemo
  // Now uses the is_qualified field from the backend to show all qualified submissions from all rounds
  const qualifiedSubmissions = useMemo(() => {
    const allSubs = gameData?.allSubmissions;

    if (!allSubs || allSubs.length === 0) {
      return [];
    }

    // Simply filter submissions that are marked as qualified
    return allSubs.filter((sub) => sub.is_qualified === true);
  }, [gameData?.allSubmissions]);

  if (loading) {
    return <div>Loading game...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>Error: {error}</div>;
  }

  if (!gameData) {
    // This case might be hit if error occurred or data was invalid
    return <div>Game data could not be loaded.</div>;
  }

  // Determine user status message
  let userStatus = "You are viewing as a guest.";
  if (currentUser) {
    if (isOwner) {
      userStatus = "You are the owner of this game.";
    } else if (isPlayer) {
      userStatus = "You are a player in this game.";
    } else {
      userStatus = "You are logged in as a viewer.";
    }
  }

  return (
    <div className="game-page">
      {!currentUser && !gameData ? (
        <div className="login-container">
          <div className="login-background">
            <div className="login-shape login-shape-primary"></div>
            <div className="login-shape login-shape-secondary"></div>
          </div>
          <div className="login-form">
            <h3>Login to Join Game</h3>
            {loginError && <div className="error-message">{loginError}</div>}
            <form onSubmit={(e) => handleLogin(e, false)}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="Email"
                id="email"
                required
              />
              <br />
              <label htmlFor="password">Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Password"
                id="password"
                required
              />
              <br />
              <button
                type="submit"
                className="login-button"
                disabled={loginLoading}
              >
                {loginLoading ? "Logging In..." : "Log In"}
              </button>
              <button
                type="button"
                className="signup-button"
                onClick={(e) => handleLogin(e, true)}
                disabled={loginLoading}
              >
                {loginLoading ? "Signing Up..." : "Sign Up"}
              </button>
            </form>
            {/* Option to view as guest */}
            <div className="guest-option">
              <p>or</p>
              <button
                className="guest-button"
                onClick={() => {
                  // Don't show login form, let them view as guest if game data loads
                  setShowLoginForm(false);
                }}
              >
                View as Guest
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Player View - Conditional Rendering Based on State */}
          {isPlayer && (
            <>
              {currentGameState === "ACTIVE" && (
                // --> Restore Submission Form Logic <--
                <div className="common-section">
                  <h4>
                    Your Submission (Round {currentRound} / Step {currentStep})
                  </h4>
                  {mySubmission ? (
                    // Display submitted text if already submitted for this step
                    <div>
                      <p>
                        <strong>You submitted:</strong>
                      </p>
                      <p className="submission-text">
                        {mySubmission.text_content}
                      </p>
                      <p className="submission-time">
                        Submitted at:{" "}
                        {new Date(mySubmission.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  ) : justSubmitted ? (
                    // Display temporary confirmation right after submitting
                    <p className="just-submitted">
                      Submitted! Waiting for confirmation...
                    </p>
                  ) : (
                    // Display submission form if not submitted yet
                    <form onSubmit={handleTextSubmit}>
                      <textarea
                        value={submissionText}
                        onChange={(e) => setSubmissionText(e.target.value)}
                        placeholder="Enter your text for this step..."
                        required
                        rows={4}
                        className="textarea"
                        disabled={isSubmitting}
                      />
                      {submitError && (
                        <p className="submit-error">{submitError}</p>
                      )}
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={isSubmitting || !submissionText.trim()}
                      >
                        {isSubmitting ? "Submitting..." : "Submit Text"}
                      </button>
                    </form>
                  )}
                </div>
                // --- End Restore Submission Form Logic ---
              )}

              {currentGameState === "ROUND_FEEDBACK" && (
                <PlayerFeedbackView
                  // Pass necessary props down
                  gameId={gameId}
                  currentRound={currentRound}
                  isSubmitting={isSubmittingFeedback} // Pass feedback submitting state
                  submitError={feedbackSubmitError} // Pass feedback error state
                  onSubmitFeedback={handleFeedbackSubmit} // Pass handler function
                  hasSubmittedFeedback={hasSubmittedFeedback} // Pass submission status
                />
              )}

              {/* ---> Add Role Display Section <--- */}
              {assignedRole && (
                <div className="role-info-box">
                  <h4>Your Role: {assignedRole.name}</h4>
                  {assignedRole.description && (
                    <p>{assignedRole.description}</p>
                  )}
                </div>
              )}
              {/* ---> End Role Display Section <--- */}

              {currentGameState === "LOBBY" && (
                <p>Waiting for the game owner to start the game...</p>
              )}
              {currentGameState === "PRELIMINARY" && (
                <p>Game is starting... Roles assigned. Waiting for Round 1.</p>
              )}
              {currentGameState === "ENDED" && (
                <p>The game has ended. Thank you for playing!</p>
              )}
            </>
          )}

          {/* Always display basic game info */}
          <GameDisplay
            game={gameData}
            isOwner={isOwner}
            isPlayer={isPlayer}
            qualifiedSubmissions={qualifiedSubmissions}
            stepTimeRemaining={stepTimeRemaining} // <-- Pass timer state
          />

          {/* Owner View - Render GameCreationForm for managing */}
          {isOwner && (
            <GameCreationForm
              gameId={gameId}
              existingGameData={gameData}
              playersList={playersList}
              playersLoading={playersLoading}
              playersError={playersError}
              fetchPlayers={fetchPlayers} // Pass fetch function
              setFullGameData={setGameData} // Pass state setter for immediate updates
            />
          )}

          {/* Non-Owner/Non-Player View (Spectator or needs to join/login) */}
          {!isOwner &&
            !isPlayer &&
            !loading &&
            currentUser &&
            currentGameState === "LOBBY" && (
              <div className="join-button-container">
                <p>{userStatus}</p>
                <button
                  onClick={handleJoinGame}
                  disabled={joinLoading}
                  className="join-button"
                >
                  {joinLoading ? "Joining..." : "Join Game as Player"}
                </button>
                {joinError && <p className="join-error">{joinError}</p>}
              </div>
            )}

          {/* Show login form overlay if guest wants to login */}
          {showLoginForm && !currentUser && (
            <div className="login-overlay">
              <div className="login-modal">
                <div className="login-modal-header">
                  <h3>Login to Join Game</h3>
                  <button
                    className="close-modal"
                    onClick={() => setShowLoginForm(false)}
                  >
                    ×
                  </button>
                </div>
                {loginError && (
                  <div className="error-message">{loginError}</div>
                )}
                <form onSubmit={(e) => handleLogin(e, false)}>
                  <label htmlFor="modal-email">Email</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="Email"
                    id="modal-email"
                    required
                  />
                  <br />
                  <label htmlFor="modal-password">Password</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Password"
                    id="modal-password"
                    required
                  />
                  <br />
                  <button
                    type="submit"
                    className="login-button"
                    disabled={loginLoading}
                  >
                    {loginLoading ? "Logging In..." : "Log In"}
                  </button>
                  <button
                    type="button"
                    className="signup-button"
                    onClick={(e) => handleLogin(e, true)}
                    disabled={loginLoading}
                  >
                    {loginLoading ? "Signing Up..." : "Sign Up"}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Guest View Notice */}
          {!currentUser && gameData && (
            <div className="guest-notice">
              <p>
                You are viewing as a guest.{" "}
                <button
                  className="login-link"
                  onClick={() => setShowLoginForm(true)}
                >
                  Login
                </button>{" "}
                to join the game.
              </p>
            </div>
          )}

          {isPlayer && (
            <>
              {/* ---> Add Leave Game Section for Players <--- */}
              <button
                onClick={handleLeaveGame}
                disabled={leaveLoading}
                className="leave-button"
              >
                {leaveLoading ? "Leaving..." : "Leave Game"}
              </button>
              {leaveError && <p className="leave-error">{leaveError}</p>}
              {/* ---> End Leave Game Section <--- */}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default GamePage;
